import React, { useEffect, useState } from "react";
import { Col, Table, Spinner, Badge, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import Swal from "sweetalert2";

const ViewPumpInternationalSalesOrder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [salesOrderData, setSalesOrderData] = useState(null);
  const [exportchargescurrency, setExportchargescurrency] = useState(0);
  const [rows, setRows] = useState([]);

  const fetchSalesOrderData = async () => {
    try {
      const response = await axios.get(`${BASE_URL}salesOrder/findById/${id}`, {
        headers: authHeader()
      });

      const data = response.data;
      setSalesOrderData(data);
      setExportchargescurrency(data.exportpreperationchargescurrency || 0);

      if (data.salesOrderItems && data.salesOrderItems.length > 0) {
        setRows(data.salesOrderItems.map(item => ({
          description: item.product?.description || '',
          productremarks: item.productremarks || "",
          quantity: item.quantity,
          unitratecurrency: item.unitratecurrency,
          totalcurrency: item.totalcurrency,
          exchangerate: item.exchangerate,
          unitRateInr: item.unitrateinr,
          totalInr: item.totalinr,
          hsnsacno: item.hsnsacno,
          partno: item.partno,
          serialnumber: item.serialnumber,
          modelnumber: item.modelnumber,
          tagnumber: item.tagnumber,
          moc: item.moc || "",
          itemcode: item.itemcode || "",
          uom: item.uom,
          currency: item.currency || ""
        })));
      }
    } catch (error) {
      console.error("Error fetching sales order data", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch sales order data. Please try again.",
      });
      navigate("/salesOrderIndexTable/1");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesOrderData();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Draft':
        return 'secondary';
      case 'Submitted':
        return 'primary';
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'danger';
      case 'Completed':
        return 'info';
      case 'Open':
        return 'warning';
      default:
        return 'light';
    }
  };

  if (isLoading) {
    return (
      <div className="container text-center py-5">
        <Spinner animation="border" />
        <p>Loading sales order details...</p>
      </div>
    );
  }

  if (!salesOrderData) {
    return (
      <div className="container text-center py-5">
        <p>No sales order data found</p>
      </div>
    );
  }

  return (
    <div className="container">
      <br />
      <h5>International Pump Sales Order Details</h5>
      <hr />
      <Form>
        {/* Header Information */}
        <Row>
          <Col md={6}><p><strong>Sales Order No:</strong> {salesOrderData.sono || '-'}</p></Col>
          <Col md={6}><p><strong>Sales Order Date:</strong> {formatDate(salesOrderData.sodate)}</p></Col>
          <Col md={6}><p><strong>Quotation No:</strong> {salesOrderData.quotationno || '-'}</p></Col>
          <Col md={6}><p><strong>Quotation Date:</strong> {formatDate(salesOrderData.quotationdate)}</p></Col>
          <Col md={6}><p><strong>Status:</strong> <Badge bg={getStatusBadge(salesOrderData.status)}>{salesOrderData.status || '-'}</Badge></p></Col>
          <Col md={6}><p><strong>Currency:</strong> {salesOrderData.salesordercurrency || '$ (USD)'}</p></Col>
          <Col md={6}><p><strong>Exchange Rate:</strong> {salesOrderData.soexchangerate || '0.00'}</p></Col>
          <Col md={6}><p><strong>Customer PO No:</strong> {salesOrderData.customerpono || '-'}</p></Col>
          <Col md={6}><p><strong>Customer PO Date:</strong> {formatDate(salesOrderData.customerpodate)}</p></Col>
          <Col md={6}><p><strong>Branch:</strong> {salesOrderData.branch?.branchname || '-'}</p></Col>
          <Col md={6}><p><strong>Warehouse:</strong> {salesOrderData.warehouse?.warehousename || '-'}</p></Col>
        </Row>

        <hr />
        <h5>Customer Details</h5>
        <hr />
        <Row>
          <Col md={6}><p><strong>Customer:</strong> {salesOrderData.customer?.companyname || '-'}</p></Col>
          <Col md={6}><p><strong>Contact Person:</strong> {salesOrderData.contactperson || '-'}</p></Col>
          <Col md={6}><p><strong>Contact No:</strong> {salesOrderData.contactno || '-'}</p></Col>
          <Col md={6}><p><strong>Email:</strong> {salesOrderData.email || '-'}</p></Col>
          <Col md={12}><p><strong>Customer Address:</strong> {salesOrderData.customeraddress?.customeraddress || '-'}</p></Col>
        </Row>

        <hr />
        <h5>Terms</h5>
        <hr />

        <Row>
          <Col md={6}><p><strong>Payment Terms:</strong> {salesOrderData.paymentterm || '-'}</p></Col>
          <Col md={6}><p><strong>Incoterms:</strong> {salesOrderData.incoterm || '-'}</p></Col>
        </Row>

        <hr />
        <h5>Branch and Warehouse Details</h5>
        <hr />
        <Row>
          <Col md={6}><p><strong>Branch:</strong> {salesOrderData.branch?.branchname || '-'}</p></Col>
          <Col md={6}><p><strong>Warehouse:</strong> {salesOrderData.warehouse?.warehousename || '-'}</p></Col>
        </Row>
        <hr />

        <h5>Sales Order Items</h5>
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Product Description</th>
                <th>Serial No</th>
                <th>Model No</th>
                <th>Tag No</th>
                <th>Part No</th>
                <th>MOC</th>
                <th>Item Code</th>
                <th>HSN Code</th>
                <th>Quantity</th>
                <th>UOM</th>
                <th>Unit Price (Currency)</th>
                <th>Total (Currency)</th>
                <th>Unit Price (INR)</th>
                <th>Total (INR)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td>
                    {row.description}
                    {row.productremarks && (
                      <div className="text-muted small mt-2">
                        <strong>Remarks:</strong> {row.productremarks}
                      </div>
                    )}
                  </td>
                  <td>{row.serialnumber || '-'}</td>
                  <td>{row.modelnumber || '-'}</td>
                  <td>{row.tagnumber || '-'}</td>
                  <td>{row.partno || '-'}</td>
                  <td>{row.moc || '-'}</td>
                  <td>{row.itemcode || '-'}</td>
                  <td>{row.hsnsacno || '-'}</td>
                  <td>{row.quantity || '0'}</td>
                  <td>{row.uom || '-'}</td>
                  <td>{row.unitratecurrency?.toFixed(2) || '0.00'}</td>
                  <td>{row.totalcurrency?.toFixed(2) || '0.00'}</td>
                  <td>{row.unitRateInr?.toFixed(2) || '0.00'}</td>
                  <td>{row.totalInr?.toFixed(2) || '0.00'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Totals Card */}
        <div className="card mt-3" style={{ width: '50%', marginLeft: 'auto' }}>
          <div className="card-header bg-light">
            <h5 className="mb-0">Sales Order Summary</h5>
          </div>
          <div className="card-body">
            <div className="row mb-2">
              <div className="col-8 text-end">
                <strong>Net Total (INR)</strong>
              </div>
              <div className="col-4">
                <strong>{salesOrderData.nettotalinr}</strong>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-8 text-end">
                <strong>Net Total (Currency)</strong>
              </div>
              <div className="col-4">
                <strong>{salesOrderData.nettotalcurrency}</strong>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-8 text-end">
                <strong>Export Charges (Currency)</strong>
              </div>
              <div className="col-4">
                <strong>{salesOrderData.exportpreperationchargescurrency?.toFixed(2) || '0.00'}</strong>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-8 text-end">
                <strong>Export Charges (INR)</strong>
              </div>
              <div className="col-4">
                <strong>{(salesOrderData.exportpreperationchargescurrency * salesOrderData.soexchangerate)?.toFixed(2) || '0.00'}</strong>
              </div>
            </div>

            <div className="row mt-3 pt-2 border-top">
              <div className="col-8 text-end">
                <h5>Grand Total (INR)</h5>
              </div>
              <div className="col-4">
                <h5>{salesOrderData.grandtotalinr?.toFixed(2) || '0.00'}</h5>
              </div>
            </div>

            <div className="row mt-2">
              <div className="col-8 text-end">
                <h5>Grand Total (Currency)</h5>
              </div>
              <div className="col-4">
                <h5>{salesOrderData.grandtotalcurrency?.toFixed(2) || '0.00'}</h5>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end mt-4">
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => navigate("/salesOrderIndexTable/1")}
          >
            BACK TO LIST
          </button>
        </div>
      </Form>
    </div>
  );
};

export default ViewPumpInternationalSalesOrder;